import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  IndianRupee, GraduationCap, Home, Utensils, FlaskConical, 
  BookOpen, Wifi, Bus, Calculator, Award, TrendingDown
} from 'lucide-react';

const FeeDetailsPopup = ({ college, isOpen, onClose }) => {
  if (!college) return null;

  // Extract fee data
  const fees = college.fees || {};
  const tuitionMin = typeof fees === 'object' ? fees.min : fees;
  const tuitionMax = typeof fees === 'object' ? fees.max : fees;
  const hostelFees = college.hostelFees || Math.round(tuitionMin * 0.25);
  const messFees = college.messFees || Math.round(tuitionMin * 0.3);
  
  // Calculate additional fees (estimates)
  const labFees = Math.round(tuitionMin * 0.05);
  const libraryFees = Math.round(tuitionMin * 0.02);
  const examFees = 5000;
  const developmentFees = Math.round(tuitionMin * 0.08);
  const transportFees = 15000;
  const miscFees = 10000;

  // Total calculations
  const annualMinTotal = tuitionMin + hostelFees + messFees + labFees + libraryFees + examFees;
  const annualMaxTotal = tuitionMax + hostelFees + messFees + labFees + libraryFees + examFees + developmentFees;
  
  const fourYearMinTotal = annualMinTotal * 4;
  const fourYearMaxTotal = annualMaxTotal * 4;

  // Format currency
  const formatCurrency = (amount) => {
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(1)}L`;
    }
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  // Fee breakdown items
  const feeBreakdown = [
    { 
      icon: GraduationCap, 
      label: 'Tuition Fee', 
      amount: `${formatCurrency(tuitionMin)} - ${formatCurrency(tuitionMax)}`,
      description: 'Per year',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    { 
      icon: Home, 
      label: 'Hostel Fee', 
      amount: formatCurrency(hostelFees),
      description: 'Per year (approx)',
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    { 
      icon: Utensils, 
      label: 'Mess/Food', 
      amount: formatCurrency(messFees),
      description: 'Per year (approx)',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
    { 
      icon: FlaskConical, 
      label: 'Lab & Practical', 
      amount: formatCurrency(labFees),
      description: 'Per year',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    { 
      icon: BookOpen, 
      label: 'Library & Books', 
      amount: formatCurrency(libraryFees),
      description: 'Per year',
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-50'
    },
    { 
      icon: Calculator, 
      label: 'Exam Fee', 
      amount: formatCurrency(examFees),
      description: 'Per year',
      color: 'text-slate-600',
      bgColor: 'bg-slate-50'
    },
  ];

  // Scholarship info based on college type
  const scholarshipInfo = {
    'IIT': [
      { name: 'Merit-cum-Means (MCM)', discount: 'Full tuition waiver', eligibility: 'Family income < ₹4.5L/year' },
      { name: 'SC/ST Scholarship', discount: 'Full fee waiver', eligibility: 'SC/ST category students' },
      { name: 'Institute Merit Award', discount: '₹25,000 - ₹1,00,000', eligibility: 'Top performers' }
    ],
    'NIT': [
      { name: 'Merit Scholarship', discount: 'Up to 100% tuition', eligibility: 'Based on JEE rank & income' },
      { name: 'SC/ST/PwD Waiver', discount: 'Full tuition waiver', eligibility: 'Reserved categories' },
      { name: 'Institute Free Ship', discount: '50-100% fees', eligibility: 'Family income < ₹5L/year' }
    ],
    'IIIT': [
      { name: 'Merit Based', discount: '25-100% tuition', eligibility: 'Top JEE performers' },
      { name: 'Need Based', discount: 'Up to 75% fees', eligibility: 'Economically weaker sections' }
    ],
    'Government': [
      { name: 'State Merit Scholarship', discount: '₹10,000 - ₹50,000', eligibility: 'State rank holders' },
      { name: 'EWS Scholarship', discount: 'Full tuition', eligibility: 'EWS category' },
      { name: 'SC/ST/OBC Scholarship', discount: 'Various', eligibility: 'Reserved categories' }
    ],
    'Private': [
      { name: 'Merit Scholarship', discount: '25-100% tuition', eligibility: 'JEE rank < 10,000' },
      { name: 'Sports/Cultural Quota', discount: '10-50% fees', eligibility: 'State/National level achievements' },
      { name: 'Sibling Discount', discount: '10% off', eligibility: 'If sibling is already enrolled' }
    ],
    'Deemed': [
      { name: 'Academic Excellence', discount: '25-50% tuition', eligibility: 'JEE rank < 20,000' },
      { name: 'Need Based Aid', discount: 'Variable', eligibility: 'Demonstrated financial need' }
    ]
  };

  const collegeScholarships = scholarshipInfo[college.type] || scholarshipInfo['Private'];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <IndianRupee className="w-6 h-6 text-green-600" />
            Fee Details - {college.name}
          </DialogTitle>
          <DialogDescription>
            {college.city}, {college.state} • {college.type}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Annual Fee Breakdown */}
          <div>
            <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <Calculator className="w-5 h-5" /> Annual Fee Breakdown
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {feeBreakdown.map((item, idx) => (
                <div key={idx} className={`p-3 rounded-lg ${item.bgColor}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <item.icon className={`w-4 h-4 ${item.color}`} />
                    <span className="text-sm font-medium text-slate-700">{item.label}</span>
                  </div>
                  <p className={`text-lg font-bold ${item.color}`}>{item.amount}</p>
                  <p className="text-xs text-slate-500">{item.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Optional Expenses */}
          <div className="bg-slate-50 rounded-lg p-4">
            <h4 className="font-medium text-slate-700 mb-2">Optional Expenses (Per Year)</h4>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Bus className="w-4 h-4 text-slate-500" />
                <span>Transport: {formatCurrency(transportFees)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Wifi className="w-4 h-4 text-slate-500" />
                <span>Internet: ₹3,000</span>
              </div>
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-slate-500" />
                <span>Misc: {formatCurrency(miscFees)}</span>
              </div>
            </div>
          </div>

          {/* Total Cost Summary */}
          <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-blue-600" /> Total Cost Estimate
              </h3>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <p className="text-sm text-slate-600 mb-1">Annual Cost</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {formatCurrency(annualMinTotal)} - {formatCurrency(annualMaxTotal)}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Including hostel & mess</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <p className="text-sm text-slate-600 mb-1">4-Year Total</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(fourYearMinTotal)} - {formatCurrency(fourYearMaxTotal)}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Complete B.Tech cost</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Scholarships */}
          <div>
            <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <Award className="w-5 h-5 text-yellow-500" /> Available Scholarships
            </h3>
            <div className="space-y-2">
              {collegeScholarships.map((scholarship, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div>
                    <p className="font-medium text-slate-800">{scholarship.name}</p>
                    <p className="text-xs text-slate-600">{scholarship.eligibility}</p>
                  </div>
                  <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                    {scholarship.discount}
                  </Badge>
                </div>
              ))}
            </div>
          </div>

          {/* Disclaimer */}
          <div className="text-xs text-slate-500 bg-slate-100 p-3 rounded-lg">
            <p><strong>Note:</strong> Fee estimates are approximate and based on publicly available data. 
            Actual fees may vary. Please verify with the college&apos;s official website or admission office 
            for the most accurate and up-to-date information.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FeeDetailsPopup;
